export const GROUPS_COLLECTION = 'groups';
export const INVITED_COLLECTION = 'invited';

/** Nombre maximum de groupes acceptés sur un même créneau */
export const MAX_GROUPS_PER_SLOT = 12;

/** Seuil d'invités au-delà duquel le groupe est signalé en rouge */
export const GUESTS_WARNING_THRESHOLD = 10;

/** Durée d'un créneau de visite, en minutes */
export const SLOT_DURATION_MINUTES = 150;

/** Élément de programme sélectionné (chaîne brute ou objet avec libellé) */
export type ProgramItem =
  | string
  | { _id?: string; label?: string; name?: string; title?: string; value?: string };

export interface Group {
  _id: string;
  privateKey: string;
  collection: string;
  /** Participant à l'origine des invitations (l'invitant) */
  parentId?: string;
  name?: string;
  /** Nom de l'invitant saisi à la main (indépendant du participant lié) */
  inviterName?: string;
  /** Format "YYYY-MM-DD HH:mm" */
  slot?: string;
  guide?: string;
  /** Démonstrations sélectionnées (multiselect) */
  demos?: ProgramItem[];
  /** Conférences sélectionnées (multiselect) */
  conferences?: ProgramItem[];
  createdAt: string;
  updatedAt: string;
}

export interface Invited {
  _id: string;
  collection: string;
  /** Groupe auquel l'invité appartient */
  parentId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  /** Statut d'inscription ; `registered` = l'invité a validé */
  status?: string;
  /** L'invité a demandé un accès PMR */
  pmr?: 'yes' | 'no';
  /** L'invité a demandé une place de parking */
  parking?: 'yes' | 'no';
  /** Plaque d'immatriculation (si parking demandé) */
  licensePlate?: string;
  /**
   * Invité saisi dans le formulaire du groupe mais sans fiche en base
   * (formulaire non validé) : il n'a ni id ni statut réels.
   */
  isDraft?: boolean;
}

/** Élément manquant sur un groupe, qui le rend "à compléter" */
export type GroupMissingField = 'slot' | 'program' | 'guests';

/** Un groupe enrichi de ses invités et de ses statistiques */
export interface GroupWithInvited extends Group {
  invited: Invited[];
  /** Nombre total d'invités du groupe */
  totalCount: number;
  /** Nombre d'invités qui ont confirmé (status === 'registered') */
  registeredCount: number;
  /** Nombre d'invités qui ont refusé (status === 'refused') */
  refusedCount: number;
  /** Nombre d'invités en attente de réponse (ni confirmé ni refusé) */
  pendingCount: number;
  /** Nombre d'invités ayant demandé un accès PMR */
  pmrCount: number;
  /** Nombre d'invités ayant demandé une place de parking */
  parkingCount: number;
  /** Nombre d'invités saisis dans le formulaire mais sans fiche en base */
  draftCount: number;
  /** Éléments manquants (créneau, programme, invités) */
  missingFields: GroupMissingField[];
  /** Le groupe a au moins un élément manquant */
  isIncomplete: boolean;
}

/** Type d'un élément de programme */
export type ProgramType = 'demo' | 'conference';

/**
 * Entité référencée par un élément de programme (item de data, session…).
 * Les champs multiselect ne stockent que des ids : ces entités sont résolues
 * dans le store pour afficher un vrai libellé.
 */
export interface ProgramEntity {
  _id?: string;
  name?: string;
  title?: string;
  label?: string;
  /** Jour de la conférence ("YYYY-MM-DD" ou date ISO) */
  startDate?: string;
  endDate?: string;
  /** Horaires de la conférence ("HH:mm") */
  startTime?: string;
  endTime?: string;
}

/** Dates ISO de début et de fin (créneau de visite, conférence…) */
export type SlotDates = { startDate?: string; endDate?: string };

/** Programme d'un groupe, prêt à afficher */
export type GroupProgram = { demos: ProgramEntry[]; conferences: ProgramEntry[] };

/** Élément de programme prêt à afficher */
export interface ProgramEntry extends SlotDates {
  id?: string;
  type: ProgramType;
  /** Nom de la démo, ou titre de la conférence */
  label: string;
  /** Conférence sans horaire connu : son jour, ex. "mardi 17 novembre 2026" */
  day?: string;
}
